import {
Avatar,
Box,
CircularProgress,
Dialog,
Divider,
Fade,
IconButton,
InputAdornment,
InputBase,
List,
ListItemButton,
ListItemText,
Paper,
Popper,
Toolbar,
Typography,
useMediaQuery,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import GroupsIcon from "@mui/icons-material/Groups";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

import { useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";

import api from "../../api/api";

const UserSearchBox = ({
setSelectedUser,
setActiveComponent,
}) => {
const [searchQuery, setSearchQuery] = useState("");
const [userResults, setUserResults] = useState([]);
const [loading, setLoading] = useState(false);
const [openDropdown, setOpenDropdown] = useState(false);
const [anchorEl, setAnchorEl] = useState(null);
const [dialogOpen, setDialogOpen] = useState(false);

const inputRef = useRef(null);
const dropdownRef = useRef(null);

const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

const ORANGE = "#F97316";
const DARK_ORANGE = "#EA580C";
const BLACK = "#111111";
const DARK_GRAY = "#1F2937";
const MEDIUM_GRAY = "#6B7280";
const LIGHT_GRAY = "#F3F4F6";
const BORDER_GRAY = "#E5E7EB";



useEffect(() => {
if (!searchQuery.trim()) {
setUserResults([]);
setOpenDropdown(false);
setLoading(false);
return;
}


const fetchUsers = async () => {
  setLoading(true);

  try {
    const token = localStorage.getItem("access");

    const response = await api.get(
      `/api/users/user-page-details/?search=${encodeURIComponent(
        searchQuery.trim()
      )}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setUserResults(response.data?.users || []);
    setOpenDropdown(true);
  } catch (error) {
    console.error("User search error:", error);

    setUserResults([]);
    setOpenDropdown(false);
  } finally {
    setLoading(false);
  }
};

const delayDebounce = setTimeout(fetchUsers, 350);

return () => clearTimeout(delayDebounce);


}, [searchQuery]);

                                                                       

useEffect(() => {
const handleClickOutside = (event) => {
if (
dropdownRef.current &&
!dropdownRef.current.contains(event.target) &&
inputRef.current &&
!inputRef.current.contains(event.target)
) {
setOpenDropdown(false);
}
};


document.addEventListener(
  "mousedown",
  handleClickOutside
);

return () => {
  document.removeEventListener(
    "mousedown",
    handleClickOutside
  );
};


}, []);



const handleItemClick = (user) => {
setSelectedUser(user);


setActiveComponent(
  "userdetailspage"
);

setSearchQuery("");
setUserResults([]);
setOpenDropdown(false);
setDialogOpen(false);


};



const handleInputClick = (event) => {
setAnchorEl(event.currentTarget);


if (userResults.length > 0) {
  setOpenDropdown(true);
}


};


const handleAvatarError = (event) => {
event.currentTarget.onerror = null;
event.currentTarget.src = "/default-avatar.png";
};



const renderSearchInput = (
<Box
sx={{
position: "relative",
width: "100%",
}}
>
<InputBase
inputRef={inputRef}
value={searchQuery}
onChange={(event) =>
setSearchQuery(event.target.value)
}
onClick={handleInputClick}
placeholder="Search staff, teachers, students or support staff..."
fullWidth
startAdornment={ <InputAdornment position="start">
<SearchIcon
sx={{
color: ORANGE,
fontSize: 24,
}}
/> </InputAdornment>
}
endAdornment={
loading ? ( <InputAdornment position="end">
<CircularProgress
size={20}
sx={{
color: ORANGE,
}}
/> </InputAdornment>
) : searchQuery ? ( <InputAdornment position="end">
<IconButton
size="small"
onClick={() => {
setSearchQuery("");
setUserResults([]);
setOpenDropdown(false);
}}
sx={{
color: MEDIUM_GRAY,


              "&:hover": {
                color: ORANGE,
                backgroundColor: "#FFF7ED",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      ) : null
    }
    sx={{
      minHeight: 48,
      px: 1.5,
      borderRadius: 2,

      backgroundColor: "#FFFFFF",

      border: "1px solid",
      borderColor: BORDER_GRAY,

      transition: "all 0.25s ease",

      "&:hover": {
        borderColor: ORANGE,
        boxShadow: "0 4px 14px rgba(249, 115, 22, 0.10)",
      },

      "&:focus-within": {
        borderColor: ORANGE,
        boxShadow:
          "0 0 0 3px rgba(249, 115, 22, 0.15)",
      },

      "& input": {
        fontSize: 14,
        color: BLACK,
        fontWeight: 500,

        "&::placeholder": {
          color: MEDIUM_GRAY,
          opacity: 1,
        },
      },
    }}
  />
</Box>


);



const renderUserItem = (user) => {
const image =
user.profile_image ||
user.image ||
"/default-avatar.png";


const name =
  user.full_name ||
  user.username ||
  "Unknown User";

const secondary =
  user.user_type ||
  user.department ||
  "User";

return (
  <ListItemButton
    key={user.id}
    onClick={() =>
      handleItemClick(user)
    }
    sx={{
      px: 2,
      py: 1.2,
      gap: 1.5,

      transition: "all 0.2s ease",

      "&:hover": {
        backgroundColor: "#FFF7ED",

        "& .user-name": {
          color: DARK_ORANGE,
        },

        "& .user-avatar": {
          borderColor: ORANGE,
        },
      },
    }}
  >
    <Avatar
      className="user-avatar"
      src={image}
      alt={name}
      onError={handleAvatarError}
      sx={{
        width: 42,
        height: 42,

        border: "2px solid",
        borderColor: BORDER_GRAY,

        transition: "all 0.2s ease",

        backgroundColor: "#2F2F2F",
      }}
    />

    <ListItemText
      primary={
        <Typography
          className="user-name"
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: BLACK,
            transition: "color 0.2s ease",
          }}
        >
          {name}
        </Typography>
      }
      secondary={
        <Typography
          sx={{
            mt: 0.3,
            fontSize: 12,
            color: MEDIUM_GRAY,
            fontWeight: 500,
          }}
        >
          {secondary}
        </Typography>
      }
    />
  </ListItemButton>
);


};



const renderResultsList = () => {
if (loading) {
return (
<Box
sx={{
minHeight: 150,
display: "flex",
flexDirection: "column",
alignItems: "center",
justifyContent: "center",
gap: 1.5,
p: 3,
}}
>
<CircularProgress
size={28}
thickness={4}
sx={{
color: ORANGE,
}}
/>


      <Typography
        sx={{
          fontSize: 13,
          color: MEDIUM_GRAY,
          fontWeight: 500,
        }}
      >
        Searching users...
      </Typography>
    </Box>
  );
}

if (
  searchQuery.trim() &&
  userResults.length === 0
) {
  return (
    <Box
      sx={{
        minHeight: 160,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        p: 3,
      }}
    >
      <PersonSearchIcon
        sx={{
          fontSize: 44,
          color: "#D1D5DB",
          mb: 1,
        }}
      />

      <Typography
        sx={{
          fontWeight: 700,
          color: DARK_GRAY,
          fontSize: 14,
        }}
      >
        No users found
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          color: MEDIUM_GRAY,
          fontSize: 12,
        }}
      >
        No results found for "{searchQuery}"
      </Typography>
    </Box>
  );
}

const users = userResults.filter(
  (user) => !user.is_supportstaff
);

const supportStaff = userResults.filter(
  (user) => user.is_supportstaff
);

return (
  <Box
    sx={{
      maxHeight: 420,
      overflowY: "auto",

      "&::-webkit-scrollbar": {
        width: 6,
      },

      "&::-webkit-scrollbar-track": {
        backgroundColor: "#F3F4F6",
      },

      "&::-webkit-scrollbar-thumb": {
        backgroundColor: ORANGE,
        borderRadius: 10,
      },
    }}
  >
    {users.length > 0 && (
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,

            px: 2,
            py: 1.2,

            backgroundColor: "#FAFAFA",

            borderBottom: "1px solid",
            borderColor: BORDER_GRAY,
          }}
        >
          <GroupsIcon
            sx={{
              fontSize: 18,
              color: ORANGE,
            }}
          />

          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: DARK_GRAY,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            Users & Staff
          </Typography>
        </Box>

        <List disablePadding>
          {users
            .slice(0, 12)
            .map(renderUserItem)}
        </List>
      </>
    )}

    {users.length > 0 &&
      supportStaff.length > 0 && (
        <Divider />
      )}

    {supportStaff.length > 0 && (
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,

            px: 2,
            py: 1.2,

            backgroundColor: "#FAFAFA",

            borderTop: "1px solid",
            borderBottom: "1px solid",

            borderColor: BORDER_GRAY,
          }}
        >
          <SupportAgentIcon
            sx={{
              fontSize: 18,
              color: ORANGE,
            }}
          />

          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: DARK_GRAY,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            Support Staff
          </Typography>
        </Box>

        <List disablePadding>
          {supportStaff
            .slice(0, 12)
            .map(renderUserItem)}
        </List>
      </>
    )}
  </Box>
);


};



if (isMobile) {
return (
<>
<IconButton
onClick={() =>
setDialogOpen(true)
}
sx={{
width: 42,
height: 42,


        color: "#FFFFFF",

        backgroundColor: ORANGE,

        "&:hover": {
          backgroundColor: DARK_ORANGE,
        },
      }}
    >
      <SearchIcon />
    </IconButton>

    <Dialog
      fullScreen
      open={dialogOpen}
      onClose={() =>
        setDialogOpen(false)
      }
      PaperProps={{
        sx: {
          backgroundColor: "#F8F9FA",
        },
      }}
    >
      <Toolbar
        sx={{
          minHeight: 64,

          backgroundColor: BLACK,

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",

          px: {
            xs: 2,
            sm: 3,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <SearchIcon
            sx={{
              color: ORANGE,
            }}
          />

          <Typography
            sx={{
              color: "#FFFFFF",
              fontWeight: 800,
              fontSize: 17,
            }}
          >
            Search Directory
          </Typography>
        </Box>

        <IconButton
          onClick={() =>
            setDialogOpen(false)
          }
          sx={{
            color: "#FFFFFF",

            "&:hover": {
              color: ORANGE,
              backgroundColor:
                "rgba(255,255,255,0.08)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>

      <Box
        sx={{
          p: 2,
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid",
          borderColor: BORDER_GRAY,
        }}
      >
        {renderSearchInput}
      </Box>

      <Box
        sx={{
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",

            borderRadius: 2,

            border: "1px solid",
            borderColor: BORDER_GRAY,

            backgroundColor: "#FFFFFF",
          }}
        >
          {renderResultsList()}
        </Paper>
      </Box>
    </Dialog>
  </>
);


}



return (
<Box
sx={{
width: {
md: 420,
lg: 560,
xl: 650,
},


    maxWidth: "100%",
  }}
>
  {renderSearchInput}

  <Popper
    open={openDropdown}
    anchorEl={anchorEl}
    placement="bottom-start"
    transition
    sx={{
      zIndex: 9999,

      width: {
        md: 420,
        lg: 560,
        xl: 650,
      },

      maxWidth: "calc(100vw - 32px)",
    }}
  >
    {({ TransitionProps }) => (
      <Fade
        {...TransitionProps}
        timeout={200}
      >
        <Paper
          ref={dropdownRef}
          elevation={8}
          sx={{
            mt: 1,

            overflow: "hidden",

            borderRadius: 2,

            border: "1px solid",
            borderColor: BORDER_GRAY,

            backgroundColor: "#FFFFFF",

            boxShadow:
              "0 12px 35px rgba(0, 0, 0, 0.18)",
          }}
        >
          {renderResultsList()}
        </Paper>
      </Fade>
    )}
  </Popper>
</Box>


);
};

export default UserSearchBox;


